import {
  getEffectiveToday
} from '../../utils/systemTime';

import {
  fetchCoursePlacementLabels,
  getStreamDisplayName
} from '../../utils/groupLabels';

import {
  useEffect,
  useState
} from 'react';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


function apiHeaders() {
  return {
    apikey: SUPABASE_KEY,
    Authorization:
      'Bearer ' + SUPABASE_KEY
  };
}


function formatDate(value) {
  if (!value) return '';

  const date =
    new Date(`${value}T12:00:00`);

  return date.toLocaleDateString(
    'en-US',
    {
      month: 'short',
      day: 'numeric'
    }
  );
}


async function fetchVotingWeeks(
  courseId,
  includeAllWeeks = false
) {
  const today =
    getEffectiveToday();

  const cqFilter =
    includeAllWeeks
      ? ''
      : '&cq_enabled=eq.true';

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}${cqFilter}&select=id,week_number,class_date&order=class_date.asc`,
    {
      headers: apiHeaders()
    }
  );

  const rows =
    await response.json();

  const allWeeks =
    Array.isArray(rows)
      ? rows
      : [];

  /*
   * Students may browse backwards through
   * all CQ classes up to the current class,
   * but not forward into future classes.
   */
  const availableWeeks =
    allWeeks.filter(
      week =>
        String(
          week.class_date || ''
        ).slice(0, 10) <= today
    );

  if (availableWeeks.length) {
    return availableWeeks;
  }

  /*
   * Before the first CQ class, show the
   * first upcoming class rather than an
   * entirely empty Voting screen.
   */
  const upcoming =
    allWeeks.find(
      week =>
        String(
          week.class_date || ''
        ).slice(0, 10) > today
    );

  return upcoming
    ? [upcoming]
    : [];
}


async function fetchStudentStream(
  studentId,
  courseId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?student_id=eq.${studentId}&course_id=eq.${courseId}&select=stream&limit=1`,
    {
      headers: apiHeaders()
    }
  );

  const data =
    await response.json();

  return data[0]?.stream ?? null;
}


/*
 * QI OFF:
 * ordinary CQ submissions.
 */
async function fetchOriginalQuestions(
  weekId,
  stream
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?week_id=eq.${weekId}&is_eliminated=eq.false&select=*,cq_votes(vote_count)&order=submitted_at.asc`,
    {
      headers: apiHeaders()
    }
  );

  const data =
    await response.json();

  if (!Array.isArray(data)) {
    return [];
  }

  const withStream =
    await Promise.all(
      data.map(async question => {
        if (!question.enrolment_id) {
          return {
            ...question,
            stream: null
          };
        }

        const enrolmentResponse =
          await fetch(
            `${SUPABASE_URL}/rest/v1/enrolments?id=eq.${question.enrolment_id}&select=stream,students(is_master)&limit=1`,
            {
              headers: apiHeaders()
            }
          );

        const enrolments =
          await enrolmentResponse.json();

        return {
          ...question,
          stream:
            enrolments[0]?.stream ??
            null,
          isMaster:
            enrolments[0]?.students?.is_master === true
        };
      })
    );

  const safeQuestions =
    withStream.filter(
      question =>
        question?.isMaster !== true
    );

  if (stream === null) {
    return safeQuestions;
  }

  const hasStreams =
    safeQuestions.some(
      question =>
        question.stream !== null
    );

  if (!hasStreams) {
    return safeQuestions;
  }

  return safeQuestions.filter(
    question =>
      question.stream === stream ||
      question.stream === null
  );
}


/*
 * QI ON:
 * submitted revisions ONLY.
 *
 * There is deliberately NO fallback
 * to the original CQ.
 */
async function fetchRevisedQuestions(
  weekId,
  stream
) {
  const assignmentsResponse =
    await fetch(
      `${SUPABASE_URL}/rest/v1/cq_question_assignments?course_week_id=eq.${weekId}&select=id,stream,cq_submissions(enrolment_id,enrolments(students(is_master)))`,
      {
        headers: apiHeaders()
      }
    );

  const assignments =
    await assignmentsResponse.json();

  if (
    !Array.isArray(assignments) ||
    assignments.length === 0
  ) {
    return [];
  }

  const assignmentIds =
    assignments
      .map(
        assignment =>
          assignment.id
      )
      .filter(Boolean)
      .join(',');

  if (!assignmentIds) {
    return [];
  }

  const improvementsResponse =
    await fetch(
      `${SUPABASE_URL}/rest/v1/cq_question_improvements?cq_question_assignment_id=in.(${assignmentIds})&submitted=eq.true&select=id,cq_question_assignment_id,revised_question,submitted_at,vote_count,is_eliminated&order=submitted_at.asc`,
      {
        headers: apiHeaders()
      }
    );

  const improvements =
    await improvementsResponse.json();

  if (!Array.isArray(improvements)) {
    return [];
  }

  const improvementByAssignment =
    new Map();

  improvements.forEach(
    improvement => {
      if (
        !improvement
          .cq_question_assignment_id
      ) {
        return;
      }

      improvementByAssignment.set(
        improvement
          .cq_question_assignment_id,
        improvement
      );
    }
  );

  const questions =
    assignments
      .filter(
        assignment =>
          assignment?.cq_submissions?.enrolments?.students?.is_master !== true
      )
      .map(assignment => {
        const improvement =
          improvementByAssignment.get(
            assignment.id
          );

        if (
          !improvement ||
          !String(
            improvement
              .revised_question ||
              ''
          ).trim()
        ) {
          return null;
        }

        if (
          improvement
            .is_eliminated === true
        ) {
          return null;
        }

        return {
          id: improvement.id,

          question_text:
            improvement
              .revised_question,

          stream:
            assignment.stream,

          vote_count:
            Math.max(
              0,
              Number(
                improvement
                  .vote_count
              ) || 0
            )
        };
      })
      .filter(Boolean);

  if (stream === null) {
    return questions;
  }

  const hasStreams =
    questions.some(
      question =>
        question.stream !== null
    );

  if (!hasStreams) {
    return questions;
  }

  return questions.filter(
    question =>
      Number(question.stream) ===
      Number(stream)
  );
}


async function fetchQuestions(
  weekId,
  stream,
  useRevisedQuestions
) {
  return useRevisedQuestions
    ? fetchRevisedQuestions(
        weekId,
        stream
      )
    : fetchOriginalQuestions(
        weekId,
        stream
      );
}


function voteCount(question) {
  /*
   * Ordinary CQ questions, including
   * instructor-added questions, store
   * their vote total in cq_votes.
   */
  if (question?.cq_votes) {
    return Math.max(
      0,
      Number(
        question.cq_votes?.[0]
          ?.vote_count ??
        question.cq_votes
          ?.vote_count ??
        0
      ) || 0
    );
  }

  /*
   * Revised QI questions store their
   * vote total directly on
   * cq_question_improvements.vote_count.
   */
  return Math.max(
    0,
    Number(
      question?.vote_count
    ) || 0
  );
}


export default function VotingDisplay({
  session,
  course
}) {
  const [weeks, setWeeks] =
    useState([]);

  const [
    currentWeekIndex,
    setCurrentWeekIndex
  ] = useState(-1);

  const [
    weekIndex,
    setWeekIndex
  ] = useState(-1);

  const [
    studentStream,
    setStudentStream
  ] = useState(null);

  const [questions, setQuestions] =
    useState([]);

  const [
    coursePlacementLabels,
    setCoursePlacementLabels
  ] = useState({
    streams: {},
    groups: {}
  });

  const [loading, setLoading] =
    useState(true);

  const [
    loadingQuestions,
    setLoadingQuestions
  ] = useState(false);

  const [error, setError] =
    useState('');

  const qiActive =
    course?.qi_enabled === true;

  const currentWeek =
    weekIndex >= 0
      ? weeks[weekIndex] || null
      : null;

  const viewingCurrent =
    weekIndex >= 0 &&
    weekIndex === currentWeekIndex;

  const courseName =
    course?.course_titles?.name ||
    course?.name ||
    'Voting';


  useEffect(() => {
    let cancelled = false;

    if (!course?.id) {
      setCoursePlacementLabels({
        streams: {},
        groups: {}
      });
      return undefined;
    }

    fetchCoursePlacementLabels(
      course.id
    )
      .then(labels => {
        if (!cancelled) {
          setCoursePlacementLabels(
            labels || {
              streams: {},
              groups: {}
            }
          );
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.error(
            'Loading Stream title failed:',
            err
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [course?.id]);

  /*
   * Initial load.
   */
  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      setError('');
      setQuestions([]);

      try {
        if (!course?.id) {
          setWeeks([]);
          setWeekIndex(-1);
          setCurrentWeekIndex(-1);
          return;
        }

        const [
          loadedWeeks,
          stream
        ] =
          await Promise.all([
            fetchVotingWeeks(
              course.id,
              course.cq_enabled === false
            ),

            session?.studentId
              ? fetchStudentStream(
                  session.studentId,
                  course.id
                )
              : Promise.resolve(null)
          ]);

        if (cancelled) {
          return;
        }

        const safeWeeks =
          Array.isArray(loadedWeeks)
            ? loadedWeeks
            : [];

        setWeeks(safeWeeks);
        setStudentStream(stream);

        if (!safeWeeks.length) {
          setWeekIndex(-1);
          setCurrentWeekIndex(-1);
          return;
        }

        /*
         * The latest accessible CQ class
         * is always the initial/current
         * voting screen.
         */
        const initialIndex =
          safeWeeks.length - 1;

        setCurrentWeekIndex(
          initialIndex
        );

        setWeekIndex(
          initialIndex
        );

      } catch (err) {
        if (cancelled) return;

        console.error(
          'Failed to load Voting:',
          err
        );

        setError(
          'Voting could not be loaded.'
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [
    course?.id,
    course?.cq_enabled,
    session?.studentId
  ]);


  /*
   * Load the selected week's questions.
   */
  useEffect(() => {
    if (!currentWeek?.id) {
      setQuestions([]);
      return undefined;
    }

    let cancelled = false;

    async function loadQuestions() {
      setLoadingQuestions(true);
      setError('');

      try {
        const loaded =
          await fetchQuestions(
            currentWeek.id,
            studentStream,
            qiActive
          );

        if (!cancelled) {
          setQuestions(
            Array.isArray(loaded)
              ? loaded
              : []
          );
        }
      } catch (err) {
        if (cancelled) return;

        console.error(
          'Failed to load voting questions:',
          err
        );

        setQuestions([]);

        setError(
          'Questions could not be loaded.'
        );
      } finally {
        if (!cancelled) {
          setLoadingQuestions(false);
        }
      }
    }

    loadQuestions();

    return () => {
      cancelled = true;
    };
  }, [
    currentWeek?.id,
    studentStream,
    qiActive
  ]);


  /*
   * Keep the displayed vote totals and
   * elimination state live.
   */
  useEffect(() => {
    if (!currentWeek?.id) {
      return undefined;
    }

    let cancelled = false;

    async function refresh() {
      try {
        const loaded =
          await fetchQuestions(
            currentWeek.id,
            studentStream,
            qiActive
          );

        if (!cancelled) {
          setQuestions(
            Array.isArray(loaded)
              ? loaded
              : []
          );
        }
      } catch (err) {
        if (!cancelled) {
          console.error(
            'Failed to refresh Voting:',
            err
          );
        }
      }
    }

    const intervalId =
      window.setInterval(
        refresh,
        5000
      );

    return () => {
      cancelled = true;

      window.clearInterval(
        intervalId
      );
    };
  }, [
    currentWeek?.id,
    studentStream,
    qiActive
  ]);


  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.loading}>
          Loading voting…
        </div>
      </div>
    );
  }


  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div style={styles.eyebrow}>
          Voting
        </div>

        <div style={styles.courseTitle}>
          {courseName}
        </div>

        <div style={styles.weekRow}>
          <button
            type="button"
            style={{
              ...styles.weekButton,
              ...(weekIndex <= 0
                ? styles.disabledButton
                : {})
            }}
            disabled={weekIndex <= 0}
            onClick={() =>
              setWeekIndex(
                index =>
                  Math.max(
                    0,
                    index - 1
                  )
              )
            }
            aria-label="Previous class"
          >
            ‹
          </button>

          <div style={styles.weekCentre}>
            <div style={styles.weekTitle}>
              {currentWeek
                ? (
                    `Week ${currentWeek.week_number} · ${formatDate(
                      currentWeek.class_date
                    )}`
                  )
                : 'No voting weeks'}
            </div>

            <div style={styles.badgeRow}>
              {viewingCurrent && (
                <span
                  style={styles.currentBadge}
                >
                  Current class
                </span>
              )}

              {qiActive && (
                <span
                  style={styles.qiBadge}
                >
                  Revised Questions
                </span>
              )}

              {studentStream !== null && (
                <span
                  style={styles.streamBadge}
                >
                  {getStreamDisplayName(
                    coursePlacementLabels,
                    studentStream
                  )}
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            style={{
              ...styles.weekButton,
              ...(weekIndex < 0 ||
              weekIndex >=
                weeks.length - 1
                ? styles.disabledButton
                : {})
            }}
            disabled={
              weekIndex < 0 ||
              weekIndex >=
                weeks.length - 1
            }
            onClick={() =>
              setWeekIndex(
                index =>
                  Math.min(
                    weeks.length - 1,
                    index + 1
                  )
              )
            }
            aria-label="Next class"
          >
            ›
          </button>
        </div>

        {!viewingCurrent &&
          currentWeekIndex >= 0 && (
          <button
            type="button"
            style={styles.returnButton}
            onClick={() =>
              setWeekIndex(
                currentWeekIndex
              )
            }
          >
            Return to current class
          </button>
        )}
      </div>

      <div style={styles.content}>
        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {loadingQuestions ? (
          <div style={styles.empty}>
            Loading questions…
          </div>
        ) : !currentWeek ? (
          <div style={styles.empty}>
            No voting classes are
            available yet.
          </div>
        ) : questions.length === 0 ? (
          <div style={styles.empty}>
            {qiActive
              ? 'No revised questions are available for this class.'
              : 'No questions are available for this class.'}
          </div>
        ) : (
          <div style={styles.questionList}>
            {questions.map(
              (question, index) => (
                <article
                  key={question.id}
                  style={styles.card}
                >
                  <div
                    style={styles.cardTop}
                  >
                    <span
                      style={
                        styles.questionNumber
                      }
                    >
                      {index + 1}
                    </span>

                    <strong
                      style={
                        styles.voteTotal
                      }
                    >
                      {voteCount(
                        question
                      )}
                    </strong>
                  </div>

                  <div
                    style={
                      styles.questionText
                    }
                  >
                    {
                      question
                        .question_text
                    }
                  </div>
                </article>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}


const styles = {
  wrap: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    background: '#102a43',
    color: 'white'
  },

  loading: {
    padding: '36px 18px',
    textAlign: 'center',
    color: '#bcccdc'
  },

  header: {
    padding: '14px 16px 16px',
    background: '#102a43',
    borderBottom:
      '1px solid #2f6f9f'
  },

  eyebrow: {
    color: '#9fb3c8',
    fontSize: '12px',
    fontWeight: '800',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '3px'
  },

  courseTitle: {
    fontSize: '19px',
    fontWeight: '800',
    lineHeight: 1.25,
    marginBottom: '14px'
  },

  weekRow: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns:
      '42px minmax(0, 1fr) 42px',
    alignItems: 'center',
    gap: '8px'
  },

  weekButton: {
    width: '42px',
    height: '42px',
    padding: 0,
    border:
      '1px solid #486581',
    borderRadius: '8px',
    background: '#163752',
    color: 'white',
    fontSize: '28px',
    lineHeight: 1,
    cursor: 'pointer'
  },

  disabledButton: {
    opacity: 0.3,
    cursor: 'default'
  },

  weekCentre: {
    minWidth: 0,
    textAlign: 'center'
  },

  weekTitle: {
    fontSize: '15px',
    fontWeight: '800',
    lineHeight: 1.3
  },

  badgeRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '5px',
    marginTop: '6px'
  },

  currentBadge: {
    display: 'inline-flex',
    padding: '3px 9px',
    borderRadius: '999px',
    background: '#2f7d5a',
    color: 'white',
    fontSize: '11px',
    fontWeight: '800'
  },

  qiBadge: {
    display: 'inline-flex',
    padding: '3px 9px',
    borderRadius: '999px',
    background: '#c45b5b',
    color: 'white',
    fontSize: '11px',
    fontWeight: '800'
  },

  streamBadge: {
    display: 'inline-flex',
    padding: '3px 9px',
    borderRadius: '999px',
    background: '#244f70',
    color: '#d9e2ec',
    fontSize: '11px',
    fontWeight: '800'
  },

  returnButton: {
    display: 'block',
    margin: '10px auto 0',
    border: 'none',
    background: 'transparent',
    color: '#9cc7e5',
    fontSize: '13px',
    fontWeight: '700',
    textDecoration: 'underline',
    cursor: 'pointer'
  },

  content: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '12px'
  },

  questionList: {
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },

  card: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '14px',
    background: '#163752',
    border:
      '2px solid #2f6f9f',
    borderRadius: '12px',
    color: 'white',
    overflowWrap: 'anywhere'
  },

  cardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'space-between',
    gap: '12px',
    marginBottom: '9px'
  },

  questionNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '30px',
    height: '30px',
    padding: '0 8px',
    boxSizing: 'border-box',
    borderRadius: '999px',
    background: '#244f70',
    color: '#d9e2ec',
    fontSize: '13px',
    fontWeight: '900'
  },

  voteTotal: {
    color: '#e94560',
    fontSize: '27px',
    fontWeight: '900',
    lineHeight: 1
  },

  questionText: {
    width: '100%',
    minWidth: 0,
    color: 'white',
    fontSize: '17px',
    lineHeight: 1.48,
    fontWeight: '500',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    wordBreak: 'normal'
  },

  empty: {
    padding: '34px 16px',
    textAlign: 'center',
    color: '#9fb3c8',
    fontSize: '15px'
  },

  error: {
    marginBottom: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    background:
      'rgba(164, 59, 59, 0.22)',
    color: '#ffd4d4',
    fontSize: '14px'
  }
};